import { useCallback, useEffect, useMemo, useState } from 'react';
import { apiRequest } from '../api/client';

const DEFAULT_PAGE_SIZE = 25;
const DEFAULT_USERS_PAGE_SIZE = 20;
const DEFAULT_ASSEMBLIES_PAGE_SIZE = 20;
const DEFAULT_EVIDENCES_PAGE_SIZE = 24;
const DEFAULT_NEWSPAPER_PAGE_SIZE = 20;

function toIsoOrUndefined(value) {
  const text = String(value || '').trim();
  if (!text) return undefined;
  const parsed = new Date(text);
  if (Number.isNaN(parsed.getTime())) return undefined;
  return parsed.toISOString();
}

function normalizeFilters(filters) {
  const from = toIsoOrUndefined(filters.from);
  const to = toIsoOrUndefined(filters.to);
  const eventType = String(filters.eventType || '').trim() || undefined;
  const userIdRaw = String(filters.userId || '').trim();
  const ceeOnly = Boolean(filters.ceeOnly);

  let userId;
  if (userIdRaw) {
    const parsed = Number(userIdRaw);
    if (Number.isInteger(parsed) && parsed > 0) userId = parsed;
  }

  return { from, to, userId, eventType, ceeOnly };
}

function defaultFilterDraft() {
  const now = new Date();
  const from = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);

  const toLocalInput = (date) => {
    const shifted = new Date(date.getTime() - date.getTimezoneOffset() * 60 * 1000);
    return shifted.toISOString().slice(0, 16);
  };

  return {
    from: toLocalInput(from),
    to: toLocalInput(now),
    userId: '',
    eventType: '',
    ceeOnly: false
  };
}

function buildQuery(paramsObj) {
  const params = new URLSearchParams();
  Object.entries(paramsObj).forEach(([key, value]) => {
    if (value === undefined || value === null || value === '') return;
    params.set(key, String(value));
  });
  const q = params.toString();
  return q ? `?${q}` : '';
}

export function useAdminDashboard(token, notify) {
  const [summary, setSummary] = useState(null);
  const [eventsResult, setEventsResult] = useState({
    page: 1,
    pageSize: DEFAULT_PAGE_SIZE,
    total: 0,
    totalPages: 1,
    items: []
  });
  const [usersResult, setUsersResult] = useState({
    page: 1,
    pageSize: DEFAULT_USERS_PAGE_SIZE,
    total: 0,
    totalPages: 1,
    items: []
  });
  const [userOptions, setUserOptions] = useState([]);
  const [assembliesResult, setAssembliesResult] = useState({
    page: 1,
    pageSize: DEFAULT_ASSEMBLIES_PAGE_SIZE,
    total: 0,
    totalPages: 1,
    items: []
  });
  const [evidencesResult, setEvidencesResult] = useState({
    page: 1,
    pageSize: DEFAULT_EVIDENCES_PAGE_SIZE,
    total: 0,
    totalPages: 1,
    items: []
  });
  const [assembliesByUserResult, setAssembliesByUserResult] = useState({ items: [] });
  const [newspaperRequestsResult, setNewspaperRequestsResult] = useState({
    page: 1,
    pageSize: DEFAULT_NEWSPAPER_PAGE_SIZE,
    total: 0,
    totalPages: 1,
    items: []
  });

  const [loadingSummary, setLoadingSummary] = useState(false);
  const [loadingEvents, setLoadingEvents] = useState(false);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [loadingAssemblies, setLoadingAssemblies] = useState(false);
  const [loadingEvidences, setLoadingEvidences] = useState(false);
  const [loadingAssembliesByUser, setLoadingAssembliesByUser] = useState(false);
  const [loadingNewspaperRequests, setLoadingNewspaperRequests] = useState(false);

  const [filtersDraft, setFiltersDraft] = useState(defaultFilterDraft);
  const [usersFiltersDraft, setUsersFiltersDraft] = useState({
    search: '',
    status: '',
    isAdmin: '',
    sortBy: 'createdAt',
    sortOrder: 'desc'
  });
  const [eventsSort, setEventsSort] = useState({ sortBy: 'createdAt', sortOrder: 'desc' });
  const [assembliesFiltersDraft, setAssembliesFiltersDraft] = useState({
    search: '',
    userId: '',
    ceeOnly: false,
    status: '',
    reportStatus: '',
    from: '',
    to: '',
    sortBy: 'scheduledDate',
    sortOrder: 'desc'
  });
  const [evidencesFiltersDraft, setEvidencesFiltersDraft] = useState({
    search: '',
    userId: '',
    fileType: '',
    from: '',
    to: '',
    sortBy: 'createdAt',
    sortOrder: 'desc'
  });
  const [newspaperFiltersDraft, setNewspaperFiltersDraft] = useState({
    search: '',
    status: '',
    from: '',
    to: ''
  });

  const appliedFilters = useMemo(() => normalizeFilters(filtersDraft), [filtersDraft]);

  const loadSummary = useCallback(
    async (filters = appliedFilters) => {
      if (!token) return;
      setLoadingSummary(true);
      try {
        const query = buildQuery({
          from: filters.from,
          to: filters.to,
          userId: filters.userId,
          ceeOnly: filters.ceeOnly ? 'true' : undefined
        });
        const data = await apiRequest(`/admin/dashboard/summary${query}`, { method: 'GET' }, token);
        setSummary(data || null);
      } catch (err) {
        notify('error', err.message || 'No se pudo cargar el resumen del dashboard.');
      } finally {
        setLoadingSummary(false);
      }
    },
    [token, notify, appliedFilters]
  );

  const loadEvents = useCallback(
    async ({ filters = appliedFilters, page = 1, pageSize = DEFAULT_PAGE_SIZE } = {}) => {
      if (!token) return;
      setLoadingEvents(true);
      try {
        const query = buildQuery({
          from: filters.from,
          to: filters.to,
          userId: filters.userId,
          ceeOnly: filters.ceeOnly ? 'true' : undefined,
          eventType: filters.eventType,
          page,
          pageSize,
          sortBy: eventsSort.sortBy,
          sortOrder: eventsSort.sortOrder
        });
        const data = await apiRequest(`/admin/dashboard/events${query}`, { method: 'GET' }, token);
        setEventsResult(
          data || {
            page,
            pageSize,
            total: 0,
            totalPages: 1,
            items: []
          }
        );
      } catch (err) {
        notify('error', err.message || 'No se pudieron cargar los eventos.');
      } finally {
        setLoadingEvents(false);
      }
    },
    [token, notify, appliedFilters, eventsSort]
  );

  const loadUsers = useCallback(
    async ({ page = 1, pageSize = DEFAULT_USERS_PAGE_SIZE, filters = {} } = {}) => {
      if (!token) return;
      setLoadingUsers(true);
      try {
        const normalizedAdmin = String(filters.isAdmin || '').trim().toLowerCase();
        const isAdmin =
          normalizedAdmin === 'true' || normalizedAdmin === 'false' ? normalizedAdmin : undefined;

        const query = buildQuery({
          page,
          pageSize,
          search: String(filters.search || '').trim() || undefined,
          status: String(filters.status || '').trim() || undefined,
          isAdmin,
          sortBy: String(filters.sortBy || '').trim() || undefined,
          sortOrder: String(filters.sortOrder || '').trim() || undefined
        });

        const data = await apiRequest(`/admin/users${query}`, { method: 'GET' }, token);
        setUsersResult(
          data || {
            page,
            pageSize,
            total: 0,
            totalPages: 1,
            items: []
          }
        );
      } catch (err) {
        notify('error', err.message || 'No se pudo cargar el listado de usuarios.');
      } finally {
        setLoadingUsers(false);
      }
    },
    [token, notify]
  );

  const loadUserOptions = useCallback(async () => {
    if (!token) return;
    try {
      const query = buildQuery({
        page: 1,
        pageSize: 200,
        sortBy: 'firstName',
        sortOrder: 'asc'
      });
      const data = await apiRequest(`/admin/users${query}`, { method: 'GET' }, token);
      setUserOptions(Array.isArray(data?.items) ? data.items : []);
    } catch (err) {
      notify('error', err.message || 'No se pudo cargar la lista de usuarios para filtros.');
    }
  }, [token, notify]);

  const loadAssemblies = useCallback(
    async ({ page = 1, pageSize = DEFAULT_ASSEMBLIES_PAGE_SIZE, filters = {} } = {}) => {
      if (!token) return;
      setLoadingAssemblies(true);
      try {
        const userIdRaw = String(filters.userId || '').trim();
        const userIdNum = Number(userIdRaw);
        const userId = Number.isInteger(userIdNum) && userIdNum > 0 ? userIdNum : undefined;

        const query = buildQuery({
          page,
          pageSize,
          search: String(filters.search || '').trim() || undefined,
          userId,
          ceeOnly: filters.ceeOnly ? 'true' : undefined,
          status: String(filters.status || '').trim() || undefined,
          reportStatus: String(filters.reportStatus || '').trim() || undefined,
          from: toIsoOrUndefined(filters.from),
          to: toIsoOrUndefined(filters.to),
          sortBy: String(filters.sortBy || '').trim() || undefined,
          sortOrder: String(filters.sortOrder || '').trim() || undefined
        });

        const data = await apiRequest(`/admin/assemblies${query}`, { method: 'GET' }, token);
        setAssembliesResult(
          data || {
            page,
            pageSize,
            total: 0,
            totalPages: 1,
            items: []
          }
        );
      } catch (err) {
        notify('error', err.message || 'No se pudo cargar el listado de asambleas.');
      } finally {
        setLoadingAssemblies(false);
      }
    },
    [token, notify]
  );

  const loadEvidences = useCallback(
    async ({ page = 1, pageSize = DEFAULT_EVIDENCES_PAGE_SIZE, filters = {} } = {}) => {
      if (!token) return;
      setLoadingEvidences(true);
      try {
        const userIdRaw = String(filters.userId || '').trim();
        const userIdNum = Number(userIdRaw);
        const userId = Number.isInteger(userIdNum) && userIdNum > 0 ? userIdNum : undefined;

        const query = buildQuery({
          page,
          pageSize,
          search: String(filters.search || '').trim() || undefined,
          userId,
          fileType: String(filters.fileType || '').trim() || undefined,
          from: toIsoOrUndefined(filters.from),
          to: toIsoOrUndefined(filters.to),
          sortBy: String(filters.sortBy || '').trim() || undefined,
          sortOrder: String(filters.sortOrder || '').trim() || undefined
        });

        const data = await apiRequest(`/admin/evidences${query}`, { method: 'GET' }, token);
        setEvidencesResult(
          data || {
            page,
            pageSize,
            total: 0,
            totalPages: 1,
            items: []
          }
        );
      } catch (err) {
        notify('error', err.message || 'No se pudo cargar la galeria de evidencias.');
      } finally {
        setLoadingEvidences(false);
      }
    },
    [token, notify]
  );

  const loadAssembliesByUser = useCallback(
    async ({ filters = {} } = {}) => {
      if (!token) return;
      setLoadingAssembliesByUser(true);
      try {
        const query = buildQuery({
          from: toIsoOrUndefined(filters.from),
          to: toIsoOrUndefined(filters.to),
          search: String(filters.search || '').trim() || undefined,
          userId: filters.userId,
          ceeOnly: filters.ceeOnly ? 'true' : undefined
        });
        const data = await apiRequest(`/admin/assemblies-by-user${query}`, { method: 'GET' }, token);
        setAssembliesByUserResult(data || { items: [] });
      } catch (err) {
        notify('error', err.message || 'No se pudo cargar el reporte por usuario.');
      } finally {
        setLoadingAssembliesByUser(false);
      }
    },
    [token, notify]
  );

  const loadNewspaperRequests = useCallback(
    async ({ page = 1, pageSize = DEFAULT_NEWSPAPER_PAGE_SIZE, filters = {} } = {}) => {
      if (!token) return;
      setLoadingNewspaperRequests(true);
      try {
        const query = buildQuery({
          page,
          pageSize,
          search: String(filters.search || '').trim() || undefined,
          status: String(filters.status || '').trim() || undefined,
          from: toIsoOrUndefined(filters.from),
          to: toIsoOrUndefined(filters.to)
        });
        const data = await apiRequest(`/admin/newspaper/requests${query}`, { method: 'GET' }, token);
        setNewspaperRequestsResult(
          data || {
            page,
            pageSize,
            total: 0,
            totalPages: 1,
            items: []
          }
        );
      } catch (err) {
        notify('error', err.message || 'No se pudo cargar el seguimiento de periodico.');
      } finally {
        setLoadingNewspaperRequests(false);
      }
    },
    [token, notify]
  );

  const updateNewspaperRequestStatus = useCallback(async (requestId, status, comment = '') => {
    if (!token) return;
    try {
      const updated = await apiRequest(
        `/admin/newspaper/requests/${requestId}/status`,
        {
          method: 'PATCH',
          body: JSON.stringify({ status, comment })
        },
        token
      );

      setNewspaperRequestsResult((prev) => ({
        ...prev,
        items: (prev.items || []).map((item) => (item.id === updated.id ? updated : item))
      }));
      notify('success', 'Estatus de solicitud actualizado.');
    } catch (err) {
      notify('error', err.message || 'No se pudo actualizar la solicitud de periodico.');
    }
  }, [token, notify]);

  const loadInitial = useCallback(async () => {
    const normalized = normalizeFilters(filtersDraft);
    await Promise.all([
      loadSummary(normalized),
      loadEvents({ filters: normalized, page: 1, pageSize: DEFAULT_PAGE_SIZE }),
      loadUsers({ page: 1, pageSize: DEFAULT_USERS_PAGE_SIZE, filters: usersFiltersDraft }),
      loadUserOptions(),
      loadAssemblies({
        page: 1,
        pageSize: DEFAULT_ASSEMBLIES_PAGE_SIZE,
        filters: assembliesFiltersDraft
      }),
      loadAssembliesByUser({
        filters: {
          from: assembliesFiltersDraft.from,
          to: assembliesFiltersDraft.to,
          search: assembliesFiltersDraft.search,
          userId: assembliesFiltersDraft.userId,
          ceeOnly: assembliesFiltersDraft.ceeOnly
        }
      }),
      loadEvidences({
        page: 1,
        pageSize: DEFAULT_EVIDENCES_PAGE_SIZE,
        filters: evidencesFiltersDraft
      }),
      loadNewspaperRequests({
        page: 1,
        pageSize: DEFAULT_NEWSPAPER_PAGE_SIZE,
        filters: newspaperFiltersDraft
      })
    ]);
  }, [
    filtersDraft,
    loadSummary,
    loadEvents,
    loadUsers,
    loadUserOptions,
    usersFiltersDraft,
    loadAssemblies,
    assembliesFiltersDraft,
    loadAssembliesByUser,
    loadEvidences,
    evidencesFiltersDraft,
    loadNewspaperRequests,
    newspaperFiltersDraft
  ]);

  const applyFilters = useCallback(async () => {
    const normalized = normalizeFilters(filtersDraft);
    await Promise.all([
      loadSummary(normalized),
      loadEvents({ filters: normalized, page: 1, pageSize: eventsResult.pageSize || DEFAULT_PAGE_SIZE })
    ]);
  }, [filtersDraft, loadSummary, loadEvents, eventsResult.pageSize]);

  const applyUsersFilters = useCallback(async () => {
    await loadUsers({
      page: 1,
      pageSize: usersResult.pageSize || DEFAULT_USERS_PAGE_SIZE,
      filters: usersFiltersDraft
    });
  }, [loadUsers, usersResult.pageSize, usersFiltersDraft]);

  const applyAssembliesFilters = useCallback(async () => {
    await Promise.all([
      loadAssemblies({
        page: 1,
        pageSize: assembliesResult.pageSize || DEFAULT_ASSEMBLIES_PAGE_SIZE,
        filters: assembliesFiltersDraft
      }),
      loadAssembliesByUser({
        filters: {
          from: assembliesFiltersDraft.from,
          to: assembliesFiltersDraft.to,
          search: assembliesFiltersDraft.search,
          userId: assembliesFiltersDraft.userId,
          ceeOnly: assembliesFiltersDraft.ceeOnly
        }
      })
    ]);
  }, [loadAssemblies, assembliesResult.pageSize, assembliesFiltersDraft, loadAssembliesByUser]);

  const applyEvidencesFilters = useCallback(async () => {
    await loadEvidences({
      page: 1,
      pageSize: evidencesResult.pageSize || DEFAULT_EVIDENCES_PAGE_SIZE,
      filters: evidencesFiltersDraft
    });
  }, [loadEvidences, evidencesResult.pageSize, evidencesFiltersDraft]);

  const applyNewspaperFilters = useCallback(async () => {
    await loadNewspaperRequests({
      page: 1,
      pageSize: newspaperRequestsResult.pageSize || DEFAULT_NEWSPAPER_PAGE_SIZE,
      filters: newspaperFiltersDraft
    });
  }, [loadNewspaperRequests, newspaperRequestsResult.pageSize, newspaperFiltersDraft]);

  useEffect(() => {
    const trimmedSearch = String(usersFiltersDraft.search || '').trim();
    if (trimmedSearch.length < 2 && trimmedSearch.length !== 0) return;

    const timeout = setTimeout(() => {
      loadUsers({
        page: 1,
        pageSize: usersResult.pageSize || DEFAULT_USERS_PAGE_SIZE,
        filters: usersFiltersDraft
      });
    }, 350);

    return () => clearTimeout(timeout);
  }, [loadUsers, usersFiltersDraft, usersResult.pageSize]);

  useEffect(() => {
    loadEvents({ filters: appliedFilters, page: 1, pageSize: eventsResult.pageSize || DEFAULT_PAGE_SIZE });
  }, [eventsSort.sortBy, eventsSort.sortOrder, loadEvents, appliedFilters, eventsResult.pageSize]);

  const changePage = useCallback(
    async (page) => {
      const nextPage = Math.max(1, Number(page) || 1);
      await loadEvents({ filters: appliedFilters, page: nextPage, pageSize: eventsResult.pageSize || DEFAULT_PAGE_SIZE });
    },
    [loadEvents, appliedFilters, eventsResult.pageSize]
  );

  const changeEventsPageSize = useCallback(
    async (pageSize) => {
      const nextSize = Math.min(200, Math.max(1, Number(pageSize) || DEFAULT_PAGE_SIZE));
      await loadEvents({ filters: appliedFilters, page: 1, pageSize: nextSize });
    },
    [loadEvents, appliedFilters]
  );

  const changeUsersPage = useCallback(
    async (page) => {
      const nextPage = Math.max(1, Number(page) || 1);
      await loadUsers({
        page: nextPage,
        pageSize: usersResult.pageSize || DEFAULT_USERS_PAGE_SIZE,
        filters: usersFiltersDraft
      });
    },
    [loadUsers, usersResult.pageSize, usersFiltersDraft]
  );

  const changeUsersPageSize = useCallback(
    async (pageSize) => {
      const nextSize = Math.min(200, Math.max(1, Number(pageSize) || DEFAULT_USERS_PAGE_SIZE));
      await loadUsers({ page: 1, pageSize: nextSize, filters: usersFiltersDraft });
    },
    [loadUsers, usersFiltersDraft]
  );

  const changeAssembliesPage = useCallback(
    async (page) => {
      const nextPage = Math.max(1, Number(page) || 1);
      await loadAssemblies({
        page: nextPage,
        pageSize: assembliesResult.pageSize || DEFAULT_ASSEMBLIES_PAGE_SIZE,
        filters: assembliesFiltersDraft
      });
    },
    [loadAssemblies, assembliesResult.pageSize, assembliesFiltersDraft]
  );

  const changeAssembliesPageSize = useCallback(
    async (pageSize) => {
      const nextSize = Math.min(200, Math.max(1, Number(pageSize) || DEFAULT_ASSEMBLIES_PAGE_SIZE));
      await loadAssemblies({ page: 1, pageSize: nextSize, filters: assembliesFiltersDraft });
    },
    [loadAssemblies, assembliesFiltersDraft]
  );

  const changeEvidencesPage = useCallback(
    async (page) => {
      const nextPage = Math.max(1, Number(page) || 1);
      await loadEvidences({
        page: nextPage,
        pageSize: evidencesResult.pageSize || DEFAULT_EVIDENCES_PAGE_SIZE,
        filters: evidencesFiltersDraft
      });
    },
    [loadEvidences, evidencesResult.pageSize, evidencesFiltersDraft]
  );

  const changeEvidencesPageSize = useCallback(
    async (pageSize) => {
      const nextSize = Math.min(200, Math.max(1, Number(pageSize) || DEFAULT_EVIDENCES_PAGE_SIZE));
      await loadEvidences({ page: 1, pageSize: nextSize, filters: evidencesFiltersDraft });
    },
    [loadEvidences, evidencesFiltersDraft]
  );

  const changeNewspaperPage = useCallback(
    async (page) => {
      const nextPage = Math.max(1, Number(page) || 1);
      await loadNewspaperRequests({
        page: nextPage,
        pageSize: newspaperRequestsResult.pageSize || DEFAULT_NEWSPAPER_PAGE_SIZE,
        filters: newspaperFiltersDraft
      });
    },
    [loadNewspaperRequests, newspaperRequestsResult.pageSize, newspaperFiltersDraft]
  );

  const changeNewspaperPageSize = useCallback(
    async (pageSize) => {
      const nextSize = Math.min(200, Math.max(1, Number(pageSize) || DEFAULT_NEWSPAPER_PAGE_SIZE));
      await loadNewspaperRequests({ page: 1, pageSize: nextSize, filters: newspaperFiltersDraft });
    },
    [loadNewspaperRequests, newspaperFiltersDraft]
  );

  const setAssembliesSort = useCallback((sortBy, sortOrder) => {
    setAssembliesFiltersDraft((prev) => ({
      ...prev,
      sortBy: String(sortBy || 'scheduledDate'),
      sortOrder: String(sortOrder || 'desc')
    }));
  }, []);

  const setUsersSort = useCallback((sortBy, sortOrder) => {
    setUsersFiltersDraft((prev) => ({
      ...prev,
      sortBy: String(sortBy || 'createdAt'),
      sortOrder: String(sortOrder || 'desc')
    }));
  }, []);

  const setEventsOrder = useCallback((sortBy, sortOrder) => {
    setEventsSort({
      sortBy: String(sortBy || 'createdAt'),
      sortOrder: String(sortOrder || 'desc')
    });
  }, []);

  const setEvidencesSort = useCallback((sortBy, sortOrder) => {
    setEvidencesFiltersDraft((prev) => ({
      ...prev,
      sortBy: String(sortBy || 'createdAt'),
      sortOrder: String(sortOrder || 'desc')
    }));
  }, []);

  function reset() {
    setSummary(null);
    setEventsResult({
      page: 1,
      pageSize: DEFAULT_PAGE_SIZE,
      total: 0,
      totalPages: 1,
      items: []
    });
    setUsersResult({
      page: 1,
      pageSize: DEFAULT_USERS_PAGE_SIZE,
      total: 0,
      totalPages: 1,
      items: []
    });
    setUserOptions([]);
    setAssembliesResult({
      page: 1,
      pageSize: DEFAULT_ASSEMBLIES_PAGE_SIZE,
      total: 0,
      totalPages: 1,
      items: []
    });
    setAssembliesByUserResult({ items: [] });
    setEvidencesResult({
      page: 1,
      pageSize: DEFAULT_EVIDENCES_PAGE_SIZE,
      total: 0,
      totalPages: 1,
      items: []
    });
    setNewspaperRequestsResult({
      page: 1,
      pageSize: DEFAULT_NEWSPAPER_PAGE_SIZE,
      total: 0,
      totalPages: 1,
      items: []
    });
    setFiltersDraft(defaultFilterDraft());
    setUsersFiltersDraft({
      search: '',
      status: '',
      isAdmin: '',
      sortBy: 'createdAt',
      sortOrder: 'desc'
    });
    setEventsSort({ sortBy: 'createdAt', sortOrder: 'desc' });
    setAssembliesFiltersDraft({
      search: '',
      userId: '',
      ceeOnly: false,
      status: '',
      reportStatus: '',
      from: '',
      to: '',
      sortBy: 'scheduledDate',
      sortOrder: 'desc'
    });
    setEvidencesFiltersDraft({
      search: '',
      userId: '',
      fileType: '',
      from: '',
      to: '',
      sortBy: 'createdAt',
      sortOrder: 'desc'
    });
    setNewspaperFiltersDraft({
      search: '',
      status: '',
      from: '',
      to: ''
    });
  }

  return {
    summary,
    eventsResult,
    usersResult,
    userOptions,
    assembliesResult,
    evidencesResult,
    loadingSummary,
    loadingEvents,
    loadingUsers,
    loadingAssemblies,
    loadingEvidences,
    loadingAssembliesByUser,
    loadingNewspaperRequests,
    assembliesByUserResult,
    newspaperRequestsResult,
    filtersDraft,
    setFiltersDraft,
    usersFiltersDraft,
    setUsersFiltersDraft,
    assembliesFiltersDraft,
    setAssembliesFiltersDraft,
    evidencesFiltersDraft,
    setEvidencesFiltersDraft,
    newspaperFiltersDraft,
    setNewspaperFiltersDraft,
    eventsSort,
    setEventsSort,
    loadInitial,
    applyFilters,
    applyUsersFilters,
    applyAssembliesFilters,
    applyEvidencesFilters,
    applyNewspaperFilters,
    changePage,
    changeEventsPageSize,
    changeUsersPage,
    changeUsersPageSize,
    changeAssembliesPage,
    changeAssembliesPageSize,
    changeEvidencesPage,
    changeEvidencesPageSize,
    changeNewspaperPage,
    changeNewspaperPageSize,
    setUsersSort,
    setEventsOrder,
    setAssembliesSort,
    setEvidencesSort,
    updateNewspaperRequestStatus,
    reset
  };
}
